import { normalizeCalendarEventColorToken } from "./calendarEventColors";
/**
 * Ore examen practic — din 30 în 30 de minute, 07:00–16:30. Lista fixă de
 * dinainte (07:00, 08:00, 08:30, 09:00, 10:00…) lăsa găuri în care nu se
 * putea pune slot; acum orice jumătate de oră e disponibilă.
 */
export const PRACTICAL_EXAM_TIME_START = "07:00";
export const PRACTICAL_EXAM_TIME_END = "16:30";
export const PRACTICAL_EXAM_TIME_STEP_MINUTES = 30;

function pad2Time(value) {
   return String(value).padStart(2, "0");
}

export function buildPracticalExamTimeOptions(
   start = PRACTICAL_EXAM_TIME_START,
   end = PRACTICAL_EXAM_TIME_END,
   stepMinutes = PRACTICAL_EXAM_TIME_STEP_MINUTES,
) {
   const toMinutes = (text) => {
      const match = String(text || "").match(/(\d{1,2}):(\d{2})/);
      if (!match) return null;
      return Number(match[1]) * 60 + Number(match[2]);
   };
   const from = toMinutes(start);
   const to = toMinutes(end);
   const step = Math.max(1, Math.trunc(Number(stepMinutes) || 30));
   if (from == null || to == null || to < from) return [];

   const out = [];
   for (let minutes = from; minutes <= to; minutes += step) {
      out.push(`${pad2Time(Math.floor(minutes / 60))}:${pad2Time(minutes % 60)}`);
   }
   return out;
}

export const PRACTICAL_EXAM_TIME_OPTIONS = buildPracticalExamTimeOptions();

/** Coloane grid selectare ore în popup-uri examen practic. */
export const PRACTICAL_EXAM_TIME_GRID_COLUMNS = 4;

export const PRACTICAL_EXAM_DEFAULT_CAPACITY = 2;
export const PRACTICAL_EXAM_MAX_CAPACITY = 3;

export const PRACTICAL_EXAM_SECTOR_OPTIONS = ["Botanica", "Buiucani", "Ciocana"];

export {
   buildCalendarComboColorCss as buildPracticalExamComboColorCss,
   CALENDAR_EVENT_COLOR_GRID_COLUMNS as PRACTICAL_EXAM_COLOR_GRID_COLUMNS,
   CALENDAR_EVENT_COLOR_HINTS as PRACTICAL_EXAM_COLOR_HINTS,
   CALENDAR_EVENT_COLOR_LABELS as PRACTICAL_EXAM_COLOR_LABELS,
   CALENDAR_EVENT_COLOR_TOKENS as PRACTICAL_EXAM_COLOR_TOKENS,
   CALENDAR_EVENT_COMBO_COLOR_TOKENS as PRACTICAL_EXAM_COMBO_COLOR_TOKENS,
   CALENDAR_EVENT_COMBO_COLORS as PRACTICAL_EXAM_COMBO_COLORS,
   calendarEventColorSwatchStyle,
   calendarEventColorToCss as practicalExamColorToCss,
   calendarEventColorTokenSuffix as practicalExamColorTokenSuffix,
   normalizeCalendarEventColorToken as normalizePracticalExamColorToken,
} from "./calendarEventColors";

// O rezervare de examen practic primește din start griul programărilor
// simple („--black-t"). Fără culoare, grila o desena cu `--default`, adică
// exact ca un loc liber, și nu se vedea că elevul e programat.
export const PRACTICAL_EXAM_RESERVATION_DEFAULT_COLOR = "--black-t";

/** Culoarea trimisă la creare/editare: gri în loc de „Implicit" sau gol. */
export function resolvePracticalExamReservationColorForSave(value) {
   const token = normalizeCalendarEventColorToken(
      value,
      PRACTICAL_EXAM_RESERVATION_DEFAULT_COLOR,
   );
   return token === "--event-default"
      ? PRACTICAL_EXAM_RESERVATION_DEFAULT_COLOR
      : token;
}

export const PRACTICAL_EXAM_STATUS_LABELS = {
   SCHEDULED: "Programat",
   PASSED: "Promovat",
   PROMOTED: "Promovat",
   PROMOVAT: "Promovat",
   ADMIS: "Promovat",
   FAILED: "Respins",
   RESPINS: "Respins",
   ABSENT: "Absent",
   CANCELLED: "Anulat",
   CANCELED: "Anulat",
   ANULAT: "Anulat",
};

const pad2 = (value) => String(value).padStart(2, "0");

function readFirstFromObject(obj, keys, fallback = "") {
   if (!obj || typeof obj !== "object") return fallback;
   for (const key of keys) {
      const value = obj[key];
      if (value !== "" && value != null) return value;
   }
   return fallback;
}

/** Normalizează orice oră la format HH:MM (păstrează și :30 etc.). */
export function normalizePracticalExamHour(value) {
   if (value == null || value === "") return "";

   const text = String(value).trim();
   const match = text.match(/(\d{1,2}):(\d{2})/);
   if (match) {
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (!Number.isFinite(hours) || hours < 0 || hours > 23) return "";
      if (!Number.isFinite(minutes) || minutes < 0 || minutes > 59) return "";
      return `${pad2(hours)}:${pad2(minutes)}`;
   }

   const asNumber = Number(text);
   if (Number.isFinite(asNumber) && asNumber >= 0 && asNumber <= 23) {
      return `${pad2(asNumber)}:00`;
   }

   return "";
}

export function isPracticalExamOnTheHour(value) {
   const match = String(value || "").match(/(\d{1,2}):(\d{2})/);
   if (!match) return false;
   return Number(match[2]) === 0;
}

export function normalizePracticalExamStatus(status) {
   return String(status || "SCHEDULED")
      .trim()
      .toUpperCase();
}

export function readPracticalExamReservationStatus(reservation) {
   return normalizePracticalExamStatus(
      readFirstFromObject(
         reservation,
         ["status", "result", "examStatus", "exam_status", "examResult", "verdict"],
         "SCHEDULED",
      ),
   );
}

export function getPracticalExamStatusLabel(status) {
   const normalized = normalizePracticalExamStatus(status);
   return PRACTICAL_EXAM_STATUS_LABELS[normalized] || normalized;
}

export function normalizePracticalExamSector(value, fallback = "Botanica") {
   const raw = String(value || "").trim();
   const lower = raw.toLowerCase();
   if (lower.includes("buiuc")) return "Buiucani";
   if (lower.includes("ciocan")) return "Ciocana";
   if (lower.includes("botan")) return "Botanica";
   if (PRACTICAL_EXAM_SECTOR_OPTIONS.includes(raw)) return raw;
   return fallback;
}

export function resolvePracticalExamReservationApiId(value) {
   const row = value && typeof value === "object" ? value : {};
   const raw = row.raw && typeof row.raw === "object" ? row.raw : {};
   const nestedReservation = readFirstFromObject(row, [
      "reservation",
      "practicalExamReservation",
      "practical_exam_reservation",
   ]);
   const nestedFromRaw = readFirstFromObject(raw, [
      "reservation",
      "practicalExamReservation",
      "practical_exam_reservation",
   ]);
   const slotReservation = readFirstFromObject(row, [
      "slotReservation",
      "slot_reservation",
   ]);
   const slotReservationRaw = readFirstFromObject(raw, [
      "slotReservation",
      "slot_reservation",
   ]);

   return readFirstFromObject(
      row,
      [
         "apiId",
         "reservationApiId",
         "reservationId",
         "reservation_id",
         "practicalExamReservationId",
         "practical_exam_reservation_id",
         "slotReservationId",
         "slot_reservation_id",
         "id",
      ],
      readFirstFromObject(
         nestedReservation,
         [
            "apiId",
            "reservationApiId",
            "id",
            "reservationId",
            "reservation_id",
            "practicalExamReservationId",
            "practical_exam_reservation_id",
         ],
         readFirstFromObject(
            nestedFromRaw,
            [
               "apiId",
               "reservationApiId",
               "id",
               "reservationId",
               "reservation_id",
               "practicalExamReservationId",
               "practical_exam_reservation_id",
            ],
            readFirstFromObject(
               slotReservation,
               ["apiId", "reservationApiId", "id", "reservationId", "reservation_id"],
               readFirstFromObject(
                  slotReservationRaw,
                  [
                     "apiId",
                     "reservationApiId",
                     "id",
                     "reservationId",
                     "reservation_id",
                  ],
                  readFirstFromObject(raw, [
                     "reservationApiId",
                     "reservationId",
                     "reservation_id",
                     "practicalExamReservationId",
                     "practical_exam_reservation_id",
                     "slotReservationId",
                     "slot_reservation_id",
                     "id",
                  ]),
               ),
            ),
         ),
      ),
   );
}
